import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    getStorage,
    ref as storageRef,
    uploadBytes,
    getDownloadURL,
} from "firebase/storage";
import {
    getDatabase,
    ref as dbRef,
    set,
    push,
    remove,
} from "firebase/database";
import Select from "react-select";
import { DocumentEditModal } from "./DocumentEditModal";
import { Helmet } from "react-helmet-async";
import { BreadcrumbComponent, BreadcrumbItem } from "../../Components/BreadCrumb";
import {useBuilding} from "../../Helpers/BuildingContext";

interface DocumentModel {
    key: string;
    name: string;
    fileType?: string;
    url?: string;
    uploadedAt?: string;
}

interface DocumentFile extends DocumentModel {
    storageFilename: string;
    fileType: string;
}

const DocumentsPage = () => {
    const [filteredFiles, setFilteredFiles] = useState<Record<string, DocumentFile>>({});
    const [isDragging, setIsDragging] = useState(false);
    const [currentType, setCurrentType] = useState("all");
    const [error, setError] = useState<string | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [showNameModal, setShowNameModal] = useState(false);
    const [editDoc, setEditDoc] = useState<null | {
        dbKey: string;
        fileData: DocumentFile;
    }>(null);
    const [selectedPdf, setSelectedPdf] = useState<string | null>(null);

    const location = useLocation();
    const pathSegments = location.pathname.split("/");
    const buildingKey = pathSegments[pathSegments.length - 2];

    const { currentBuilding, loading, error: buildingError, fetchBuilding } = useBuilding();

    const documents: Record<string, DocumentModel> = currentBuilding?.model?.documents || {};

    useEffect(() => {
        fetchBuilding(buildingKey).then();
    }, [buildingKey, fetchBuilding]);

    const fileTypeOptions = [
        { value: "all", label: "Alla filer" },
        { value: "documents", label: "Dokument" },
        { value: "blueprints", label: "Ritningar" },
        { value: "expert", label: "Expertutredningar" },
    ];

    useEffect(() => {
        const filteredEntries = Object.entries(documents).filter(([dbKey, file]) => {
            if (currentType === "all") return true;
            return file.fileType === currentType;
        });
        setFilteredFiles(Object.fromEntries(filteredEntries) as Record<string, DocumentFile>);
    }, [documents, currentType]);

    const totalFiles = Object.keys(documents).length;
    const typeCount = Object.values(documents).reduce(
        (acc: Record<string, number>, file: DocumentModel) => {
            const fileType = file.fileType || 'unknown';
            acc[fileType] = (acc[fileType] || 0) + 1;
            return acc;
        },
        {}
    );

    const updatedFileTypeOptions = fileTypeOptions.map((option) => {
        if (option.value === "all") {
            return {
                ...option,
                label: `${option.label} (${totalFiles})`,
            };
        }
        return {
            ...option,
            label: `${option.label} (${typeCount[option.value] || 0})`,
        };
    });

    const handleDeleteDoc = async () => {
        if (!editDoc) return;

        const db = getDatabase();
        const { dbKey } = editDoc;
        try {
            const docRef = dbRef(db, `buildings/${buildingKey}/documents/${dbKey}`);
            await remove(docRef);

            fetchBuilding(buildingKey).then();
        } catch (error) {
            console.error("Error deleting file:", error);
            setError("Kunde inte radera filen. Försök igen.");
        } finally {
            setEditDoc(null);
        }
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragging(false);

        const file = event.dataTransfer.files[0];
        if (!file) return;

        setPendingFile(file);
        setShowNameModal(true);
    };

    const generateUniqueFileName = (originalName: string): string => {
        const lastDotIndex = originalName.lastIndexOf('.');
        const nameWithoutExt = lastDotIndex !== -1 ? originalName.slice(0, lastDotIndex) : originalName;
        const extension = lastDotIndex !== -1 ? originalName.slice(lastDotIndex) : '';
        const randomString = Math.random().toString(36).substring(2, 7);
        return `${nameWithoutExt}_${randomString}${extension}`;
    };

    const handleConfirmName = async (customName: string, fileType: string) => {
        setError(null);
        if (!pendingFile) return;

        const storage = getStorage();
        const db = getDatabase();

        try {
            const documentsRef = dbRef(db, `buildings/${buildingKey}/documents`);

            setShowNameModal(false);
            const newDocRef = push(documentsRef);
            const uniqueFileName = generateUniqueFileName(pendingFile.name);

            const storageFileRef = storageRef(
                storage,
                `${buildingKey}/${uniqueFileName}`
            );

            await uploadBytes(storageFileRef, pendingFile);
            const downloadURL = await getDownloadURL(storageFileRef);

            if (!newDocRef.key) {
                throw new Error("Failed to generate document key");
            }

            const docData: DocumentModel = {
                key: newDocRef.key,
                name: customName,
                fileType,
                url: downloadURL,
                uploadedAt: new Date().toISOString(),
            };

            await set(newDocRef, docData);

            fetchBuilding(buildingKey).then();
        } catch (error) {
            setError(
                "Något gick fel vid uppladdning. Kontrollera att det inte redan finns en fil med samma namn."
            );
            console.error("Error uploading file:", error);
        } finally {
            setPendingFile(null);
        }
    };

    const handleCancelName = () => {
        setShowNameModal(false);
        setPendingFile(null);
    };

    const handleEditClick = (dbKey: string, fileData: any) => {
        setEditDoc({ dbKey, fileData });
    };

    const handleEditConfirm = async (newName: string, newType: string) => {
        if (!editDoc) return;
        try {
            const db = getDatabase();
            const docRef = dbRef(
                db,
                `buildings/${buildingKey}/documents/${editDoc.dbKey}`
            );

            const updatedDoc = {
                ...editDoc.fileData,
                name: newName,
                fileType: newType,
            };

            await set(docRef, updatedDoc);

            fetchBuilding(buildingKey).then();
        } catch (error) {
            console.error("Error updating file:", error);
            setError("Något gick fel vid uppdateringen.");
        } finally {
            setEditDoc(null);
        }
    };

    const breadcrumbItems: BreadcrumbItem[] = [
        { text: currentBuilding?.summary.name ?? "Byggnad", link: `/building/${buildingKey}` },
        { text: 'Ritningar & dokument' }
    ];

    // Show loading state while fetching building data
    if (loading) {
        return <div>Loading...</div>;
    }

    // Show error if building data fetch failed
    if (buildingError) {
        return <div style={{ color: "red" }}>{buildingError}</div>;
    }

    return (
        <div style={{ position: "relative" }}>
            <Helmet>
                <title>
                    {`Ritningar & dokument`}
                </title>
            </Helmet>
            {error && <div style={{ color: "red" }}>{error}</div>}

            <BreadcrumbComponent
                items={breadcrumbItems}
            />

            <DocumentEditModal
                fileName={pendingFile?.name || ""}
                visible={showNameModal}
                onConfirm={handleConfirmName}
                onCancel={handleCancelName}
                fileTypeOptions={fileTypeOptions}
                isEditMode={false}
            />

            <DocumentEditModal
                fileName={editDoc?.fileData.name || ""}
                visible={!!editDoc}
                onConfirm={handleEditConfirm}
                onCancel={() => setEditDoc(null)}
                fileTypeOptions={fileTypeOptions}
                isEditMode={true}
                onDelete={handleDeleteDoc}
            />

            <div
                style={{
                    display: "flex",
                    height: "100vh",
                    marginTop: '20px',
                    overflow: "hidden",
                    pointerEvents: showNameModal || editDoc ? "none" : "auto",
                    filter:
                        showNameModal || editDoc ? "grayscale(50%) brightness(80%)" : "none",
                }}
            >
                <div
                    style={{
                        width: "30%",
                        maxWidth: "350px",
                        padding: "10px",
                        backgroundColor: isDragging ? "#e0f7fa" : "#f4f4f4",
                        overflowY: "auto",
                        border: isDragging ? "2px dashed #3cb6df" : "1px solid #ddd",
                    }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                    }}
                    onDrop={handleDrop}
                    onDragLeave={() => setIsDragging(false)}
                >
                    <h3>Ritningar & dokument</h3>
                    <Select
                        options={updatedFileTypeOptions}
                        defaultValue={updatedFileTypeOptions[0]}
                        value={updatedFileTypeOptions.find((opt) => opt.value === currentType)}
                        onChange={(selectedOption) =>
                            setCurrentType((selectedOption as any).value)
                        }
                        isDisabled={showNameModal || !!editDoc}
                    />

                    <p style={{ fontStyle: "italic", marginTop: "15px" }}>
                        Släppa filer här
                        <br />
                        för att ladda upp
                    </p>

                    <ul style={{ listStyleType: "none", padding: 0 }}>
                        {Object.entries(filteredFiles).map(([dbKey, file]) => (
                            <div
                                key={dbKey}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    marginBottom: "5px",
                                }}
                            >
                                <li
                                    style={{
                                        padding: "10px",
                                        backgroundColor:
                                            selectedPdf === file.url ? "#3cb6df" : "#fff",
                                        color: selectedPdf === file.url ? "#fff" : "#000",
                                        cursor: "pointer",
                                        borderRadius: "5px",
                                        border: "1px solid #ddd",
                                        flexGrow: 1,
                                    }}
                                    onClick={() => setSelectedPdf(file.url || null)}
                                >
                                    {file.name}
                                </li>

                                <button
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor: "#f0f0f0",
                                        border: "1px solid #ddd",
                                        borderRadius: "5px",
                                        cursor: "pointer",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditClick(dbKey, file);
                                    }}
                                >
                                    Edit
                                </button>
                            </div>
                        ))}
                    </ul>
                </div>

                <div style={{ flex: 1, padding: "10px", overflowY: "auto" }}>
                    {selectedPdf ? (
                        <iframe
                            src={selectedPdf}
                            style={{ width: "100%", height: "100%" }}
                            title="PDF Viewer"
                        />
                    ) : (
                        <p>Välj en fil att förhandsvisa</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DocumentsPage;